import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Alert } from "react-bootstrap";

import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import FloatingLabelInput from "../restaurant/PlaceHolder";
import styles from "../restaurant/css/SignIn.module.css";
import loginHero from "../restaurant/assets/maya-business-login-hero.png";
import MayaBrandMark from "../components/MayaBrandMark";
import { ORGANIZATION_API_PATHS } from "../constants/apiPaths";
import { storeLoginTokens } from "../utils/sessionManager";
import { requestPermissionAndSendToken } from "../firebase-config";
import {
  applyPartnerSession,
  PARTNER_CHOOSE_BUSINESS_PATH,
  storePendingPartnerLogin,
} from "./partnerSession";
import "./PartnerLogin.css";

export const PARTNER_LOGIN_PATH = "/partner/login";

const PartnerLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const finishLogin = (response, sessionUser) => {
    const token =
      response.headers?.["x-auth-token"] ||
      response.headers?.["X-Auth-Token"] ||
      response.headers?.get?.("X-Auth-Token");

    if (!token) {
      setError("Login failed: auth token missing.");
      return;
    }

    storeLoginTokens(response);
    applyPartnerSession(sessionUser);
    if (typeof onLogin === "function") onLogin();
    requestPermissionAndSendToken().catch(() => {});

    const homePath = sessionUser.homePath || PARTNER_LOGIN_PATH;
    navigate(homePath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axiosRestaurantInstance.post(
        ORGANIZATION_API_PATHS.partnerLogin,
        {
          identifier,
          email: identifier,
          userName: identifier,
          mobile: identifier,
          password,
        },
        { skipAuthRefresh: true }
      );

      const data = response.data || {};
      if (data.requiresContextSelection && Array.isArray(data.contexts)) {
        storePendingPartnerLogin({ identifier, password, contexts: data.contexts });
        navigate(PARTNER_CHOOSE_BUSINESS_PATH);
        return;
      }

      if (!data.sessionUser) {
        setError(data.message || "Login failed.");
        return;
      }

      finishLogin(response, data.sessionUser);
    } catch (err) {
      const baseURL = process.env.REACT_APP_API_COMPANY_URL || "http://127.0.0.1:5001";
      if (!err?.response) {
        setError(
          `Cannot reach company API at ${baseURL}. Start eats-company-api and check .env.local.`
        );
      } else {
        setError(
          err.response.data?.message ||
            err.response.data?.error ||
            `Login failed (${err.response.status}).`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.signin_container}>
      <div className={styles.signin_leftSection}>
        <img
          src={loginHero}
          alt="Eats and grocery business operations"
          className={styles.signin_hero}
        />
        <div className={styles.signin_leftOverlay}>
          <p className={styles.signin_leftEyebrow}>Partner access for</p>
          <h1 className={styles.signin_leftTitle}>Food &amp; Grocery Business</h1>
          <p className={styles.signin_leftSubtitle}>
            One login for My Eats, restaurants, stores, and organizations.
          </p>
        </div>
      </div>

      <div className={styles.signin_rightSection}>
        <div className={styles.signin_form}>
          <div className="partner-login-brand">
            <MayaBrandMark variant="login" />
            <h1 className="partner-login-heading">Partner Login</h1>
          </div>

          {error ? (
            <Alert variant="danger" style={{ marginBottom: "20px" }}>
              {error}
            </Alert>
          ) : null}

          <Form onSubmit={handleSubmit}>
            <FloatingLabelInput
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              label="Email or mobile"
              isRequired={true}
            />
            <br />
            <FloatingLabelInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              isRequired={true}
            />
            <br />
            <Button type="submit" disabled={loading} className={styles.button1}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
